const FACETS_SLIDE_DURATION = 400;

// <details> gives no transition of its own, so the panel height, padding and
// opacity are driven here and the inline styles are dropped once it settles.
const facetsSlide = (element, isOpening, onDone) => {
  // A reversal mid-flight starts from whatever is on screen right now
  const running = element.facetsSlideStop
    ? window.getComputedStyle(element)
    : null;
  const from = running
    ? {
        height: `${element.getBoundingClientRect().height}px`,
        opacity: running.opacity,
        paddingTop: running.paddingTop,
        paddingBottom: running.paddingBottom,
      }
    : null;
  element.facetsSlideStop?.();

  const styles = window.getComputedStyle(element);
  const paddingTop = styles.paddingTop;
  const paddingBottom = styles.paddingBottom;
  const openHeight = element.scrollHeight;

  // The start values are laid out with transitions off, so only the move to the
  // target values animates
  element.style.transition = "none";
  element.style.overflow = "hidden";
  element.style.height = from
    ? from.height
    : isOpening
      ? "0px"
      : `${openHeight}px`;
  element.style.opacity = from ? from.opacity : isOpening ? "0" : "1";
  element.style.paddingTop = from
    ? from.paddingTop
    : isOpening
      ? "0px"
      : paddingTop;
  element.style.paddingBottom = from
    ? from.paddingBottom
    : isOpening
      ? "0px"
      : paddingBottom;

  void element.offsetHeight;

  element.style.transition = `height ${FACETS_SLIDE_DURATION}ms ease-in-out, padding ${FACETS_SLIDE_DURATION}ms ease-in-out, opacity ${FACETS_SLIDE_DURATION}ms ease-in-out`;

  let timer = null;

  const cleanup = (isDone) => {
    element.facetsSlideStop = null;
    clearTimeout(timer);
    element.removeEventListener("transitionend", onTransitionEnd);
    [
      "height",
      "opacity",
      "overflow",
      "padding-top",
      "padding-bottom",
      "transition",
    ].forEach((property) => element.style.removeProperty(property));
    if (isDone) onDone?.();
  };

  function onTransitionEnd(event) {
    if (event.target !== element || event.propertyName !== "height") return;
    cleanup(true);
  }

  element.style.height = isOpening ? `${openHeight}px` : "0px";
  element.style.opacity = isOpening ? "1" : "0";
  element.style.paddingTop = isOpening ? paddingTop : "0px";
  element.style.paddingBottom = isOpening ? paddingBottom : "0px";

  timer = setTimeout(() => cleanup(true), FACETS_SLIDE_DURATION + 100);
  element.addEventListener("transitionend", onTransitionEnd);
  element.facetsSlideStop = () => cleanup(false);
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.closeFacets = this.closeFacets.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetButtons = document.querySelectorAll(".facets-button-show");
    this.productGrid = document.getElementById("ProductGridContainer");
    this.facetForm = this.querySelector("form");
    this.facetsWrapper = this.querySelector(".facets__wrapper");
    this.closeButton = this.querySelector(".facets__close");
    this.facetsOverlay = this.querySelector(".facets__overlay");

    this.facetForm?.addEventListener("input", (event) => {
      if (event.target.closest("price-range")) return;
      this.debouncedOnSubmit(event);
    });

    this.facetForm?.addEventListener("change", (event) => {
      if (!event.target.closest("price-range")) return;
      this.debouncedOnSubmit(event);
    });

    this.facetsWrapper?.addEventListener(
      "keydown",
      this.onHandleKeyDown.bind(this)
    );

    this.facetButtons.forEach((button) =>
      button.addEventListener("click", this.handleShowButtonsClick.bind(this))
    );

    this.closeButton?.addEventListener("click", this.closeFacets);
    this.facetsOverlay?.addEventListener("click", this.closeFacets);

    this.initFilterAnimation();

    if (this.hasAttribute("data-single-open")) this.initSingleOpenFilters();

    this.restoreOpenState();
  }

  // Identifies the panel the state was saved from: its filter set plus the two
  // merchant toggles. A saved state from a different set — or from before the
  // merchant changed a setting — is ignored, so the rendered defaults win.
  get openStateSignature() {
    const filters = Array.from(
      this.querySelectorAll("details.facets__disclosure")
    ).map((details) => {
      const field = details.querySelector("[name]");
      return `${details.dataset.index}:${field ? field.name : ""}`;
    });

    return [
      this.hasAttribute("data-default-open") ? 1 : 0,
      this.hasAttribute("data-single-open") ? 1 : 0,
      filters.join(","),
    ].join("|");
  }

  readOpenState() {
    try {
      const saved = JSON.parse(
        sessionStorage.getItem(FacetFiltersForm.openStateKey) || "null"
      );
      if (!saved || saved.signature !== this.openStateSignature) return null;
      return saved.open;
    } catch (error) {
      return null;
    }
  }

  saveOpenState() {
    const open = Array.from(this.querySelectorAll("details.facets__disclosure"))
      .filter((details) => details.open)
      .map((details) => details.dataset.index);

    try {
      sessionStorage.setItem(
        FacetFiltersForm.openStateKey,
        JSON.stringify({ signature: this.openStateSignature, open })
      );
    } catch (error) {
      // storage unavailable — the panel just falls back to its default state
    }
  }

  restoreOpenState() {
    const saved = this.readOpenState();
    if (!saved) return;

    const isSingleOpen = this.hasAttribute("data-single-open");
    let opened = 0;

    this.querySelectorAll("details.facets__disclosure").forEach((details) => {
      const shouldOpen =
        saved.includes(details.dataset.index) && (!isSingleOpen || !opened);
      details.open = shouldOpen;
      if (shouldOpen) opened += 1;
    });
  }

  // The native toggle snaps open, so the click drives the slide instead
  initFilterAnimation() {
    this.addEventListener("click", (event) => {
      const summary = event.target.closest(".facets__summary");
      const details = summary?.closest(".facets__disclosure");
      if (!details || !this.contains(details)) return;
      if (event.target.closest(".facets__summary-reset")) return;

      event.preventDefault();

      if (details.open) {
        this.closeFilter(details);
      } else {
        this.openFilter(details);
      }
    });
  }

  openFilter(details) {
    const content = details.querySelector(".facets__display");
    delete details.dataset.closing;
    details.open = true;
    this.saveOpenState();
    if (content) facetsSlide(content, true);
  }

  closeFilter(details) {
    const content = details.querySelector(".facets__display");
    if (!content) {
      details.open = false;
      this.saveOpenState();
      return;
    }

    if (details.dataset.closing === "true") return;

    details.dataset.closing = "true";
    facetsSlide(content, false, () => {
      delete details.dataset.closing;
      details.open = false;
      this.saveOpenState();
    });
  }

  // Optional accordion mode: opening one filter closes the one that was open.
  // `toggle` does not bubble, so the container listens in the capture phase.
  initSingleOpenFilters() {
    // "Expanded by default" owns the first paint; the accordion takes over
    // from the first toggle.
    if (!this.hasAttribute("data-default-open")) {
      this.querySelectorAll("details.facets__disclosure[open]").forEach(
        (item, index) => {
          if (index > 0) item.open = false;
        }
      );
    }

    this.addEventListener(
      "toggle",
      (event) => {
        const details = event.target;
        if (
          !details.open ||
          !details.classList?.contains("facets__disclosure")
        ) {
          return;
        }

        this.querySelectorAll(".facets__disclosure[open]").forEach((item) => {
          if (item !== details) this.closeFilter(item);
        });
      },
      true
    );
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static setLoading(isLoading) {
    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.toggle("loading", isLoading);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.abortController?.abort();
      FacetFiltersForm.abortController = null;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.abortController = new AbortController();

    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Error fetching filters:", error);
        FacetFiltersForm.setLoading(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    const parsedContainer = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer");
    if (!container || !parsedContainer) return;
    container.innerHTML = parsedContainer.innerHTML;

    if (typeof initCursorFollow === "function") {
      container
        .querySelectorAll("[data-cursor-follow]")
        .forEach(initCursorFollow);
    }

    if (
      document.querySelector(".js-load-more") ||
      document.querySelector(".js-infinite-scroll")
    ) {
      initLoadMore();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const focused = document.activeElement;
    const focusedFilter = focused?.closest?.(".js-filter[data-index]");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormProductType .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) =>
        !matchesIndex(element) ||
        element.closest("#FacetFiltersFormProductType")
    );

    const liveCounts = new Map();
    document.querySelectorAll(".js-filter[data-index]").forEach((filter) => {
      const badge = filter.querySelector("[data-facet-count]");
      if (badge) liveCounts.set(filter.dataset.index, badge);
    });

    const sourceCurrentFilter =
      Array.from(facetDetailsElements).find(matchesIndex);
    const targetCurrentFilter = event
      ? event.target.closest(".js-filter")
      : null;

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (!target) return;
      target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderRefreshableParts(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (sourceCurrentFilter && targetCurrentFilter) {
      FacetFiltersForm.updateCurrentFilter(
        sourceCurrentFilter,
        targetCurrentFilter
      );
    }

    FacetFiltersForm.renderFilterCounts(liveCounts);
    FacetFiltersForm.restoreFilterFocus(focused, focusedFilter);

    FacetFiltersForm.setLoading(false);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const destinationElement = document.querySelector(selector);
      if (!activeFacetsElement || !destinationElement) return;
      destinationElement.innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static renderRefreshableParts(html) {
    document.querySelectorAll("[data-facets-refresh]").forEach((element) => {
      const source = html.querySelector(
        `[data-facets-refresh="${element.dataset.facetsRefresh}"]`
      );
      element.innerHTML = source ? source.innerHTML.trim() : "";
    });
  }

  static renderProductCount(html) {
    const sourceCount = html.getElementById("ProductCount");
    const destinationCount = document.getElementById("ProductCount");
    if (sourceCount && destinationCount) {
      destinationCount.innerHTML = sourceCount.innerHTML;
    }
  }

  static renderAdditionalElements(html) {
    const sourceCount = html?.querySelector("[data-facets-count]");
    if (!sourceCount) return;

    const nextCount = parseInt(sourceCount.textContent.trim(), 10) || 0;

    document
      .querySelectorAll("[data-facets-count]")
      .forEach((element) => FacetFiltersForm.updateCount(element, nextCount));

    const clearDisabled = nextCount === 0;
    document.querySelectorAll(".facets__clear").forEach((element) => {
      element.classList.toggle("facets__clear--disabled", clearDisabled);

      // Mirrors the Liquid state: a disabled Clear is announced but is not a tab stop
      const link = element.querySelector("a");
      if (!link) return;
      if (clearDisabled) {
        link.setAttribute("tabindex", "-1");
        link.setAttribute("aria-disabled", "true");
      } else {
        link.removeAttribute("tabindex");
        link.removeAttribute("aria-disabled");
      }
    });
  }

  static renderFilterCounts(liveCounts) {
    document.querySelectorAll(".js-filter[data-index]").forEach((filter) => {
      const rendered = filter.querySelector("[data-facet-count]");
      if (!rendered) return;

      const nextCount = FacetFiltersForm.readCount(rendered);
      const live = liveCounts.get(filter.dataset.index);
      if (live && live !== rendered) rendered.replaceWith(live);

      FacetFiltersForm.updateCount(live || rendered, nextCount);
    });
  }

  static updateCount(badge, nextCount) {
    const previousCount = FacetFiltersForm.readCount(badge);
    badge.classList.toggle("hidden", nextCount === 0);
    if (nextCount === previousCount) return;

    // A chip on its way out is `display: none`, and animations do not run in a
    // hidden subtree, so its value is swapped outright.
    if (nextCount === 0) {
      FacetFiltersForm.setCount(badge, nextCount);
      return;
    }

    // The badge pops in when the first filter is applied, then counts up.
    if (previousCount === 0) {
      FacetFiltersForm.setCount(badge, nextCount);
      badge.classList.remove("badge--count-in");
      // Reading the offset restarts the animation on a badge that just appeared.
      void badge.offsetWidth;
      badge.classList.add("badge--count-in");
      const clearReveal = (event) => {
        if (event.target !== badge) return;
        badge.classList.remove("badge--count-in");
        badge.removeEventListener("animationend", clearReveal);
      };
      badge.addEventListener("animationend", clearReveal);
      return;
    }

    FacetFiltersForm.rollCount(badge, nextCount, nextCount > previousCount);
  }

  // The newest value is the last one in the chip; the others are mid-animation.
  static countValue(badge) {
    const values = badge.querySelectorAll(".badge__count-value");
    return values[values.length - 1] || null;
  }

  static readCount(badge) {
    const value = FacetFiltersForm.countValue(badge) || badge;
    return parseInt(value.textContent.trim(), 10) || 0;
  }

  static setCount(badge, count) {
    const value = FacetFiltersForm.countValue(badge);
    if (!value) {
      badge.textContent = count;
      return;
    }

    FacetFiltersForm.keepSingleValue(badge, value);
    value.textContent = count;
  }

  static keepSingleValue(badge, value) {
    badge
      .querySelectorAll(".badge__count-value")
      .forEach((node) => node !== value && node.remove());
    value.className = "badge__count-value";
  }

  // The next value travels in from the side the count moved towards while the
  // previous one leaves the same way, so the chip reads as an odometer.
  static rollCount(badge, nextCount, isUp) {
    const current = FacetFiltersForm.countValue(badge);
    if (!current) {
      FacetFiltersForm.setCount(badge, nextCount);
      return;
    }

    FacetFiltersForm.keepSingleValue(badge, current);

    const direction = isUp ? "up" : "down";
    const next = document.createElement("span");
    next.className = `badge__count-value badge__count-value--enter-${direction}`;
    next.textContent = nextCount;

    current.classList.add(`badge__count-value--leave-${direction}`);
    current.addEventListener("animationend", () => current.remove(), {
      once: true,
    });

    next.addEventListener(
      "animationend",
      () => {
        next.className = "badge__count-value";
      },
      { once: true }
    );

    badge.appendChild(next);
  }

  // Re-rendering a filter group throws away the control that was operated, so
  // keyboard focus is put back on its counterpart in the fresh markup
  static restoreFilterFocus(focused, focusedFilter) {
    if (!focusedFilter || !focused || document.contains(focused)) return;

    const container = document.querySelector(
      `.js-filter[data-index="${focusedFilter.dataset.index}"]`
    );
    if (!container) return;

    let next = null;
    if (focused.id) {
      next = container.querySelector(`#${CSS.escape(focused.id)}`);
    }
    if (!next && focused.classList.contains("tab-bar__item--static")) {
      next = container.querySelector(".tab-bar__item--static");
    }

    next?.focus({ preventScroll: true });
  }

  static updateCurrentFilter(source, target) {
    const sourceSummary = source.querySelector(".facets__summary-inner");
    const targetSummary = target.querySelector(".facets__summary-inner");
    if (sourceSummary && targetSummary) {
      targetSummary.innerHTML = sourceSummary.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return [];
    return [{ section: productGrid.dataset.id }];
  }

  createSearchParams(form) {
    const params = new URLSearchParams();

    new FormData(form).forEach((value, key) => {
      if (typeof value === "string" && value.trim() === "") return;
      params.append(key, value);
    });

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    const isQuickFilter = this.facetForm?.id === "FacetFiltersFormProductType";
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm") {
        if (form.id === "FacetFiltersForm" && isQuickFilter) {
          // Strip all params the quick filter controls (by input name, not current value) so unchecked state also removes the param from the main form
          const quickParamNames = new Set(
            Array.from(this.facetForm.querySelectorAll("input[name]")).map(
              (input) => input.name
            )
          );
          const mainParams = new URLSearchParams(this.createSearchParams(form));
          for (const key of quickParamNames) {
            mainParams.delete(key);
          }
          const paramStr = mainParams.toString();
          if (paramStr) forms.push(paramStr);
        } else {
          forms.push(this.createSearchParams(form));
        }
      }
      if (form.id === "FacetFiltersFormProductType" && isQuickFilter) {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }

  handleFacetButtonClick(event) {
    event.preventDefault();

    if (!this.facetButtons.length > 0 || !this.facetForm) return;

    this.facetsWrapper.classList.add("active");

    this.focusElement = event.target.closest(".facets-button-show")
      ? event.target
      : document.activeElement;

    if (this.classList.contains("facets--drawer")) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.add("overflow-hidden-mobile-facets");
    }

    this.addEventListener(
      "transitionend",
      () => {
        trapFocus(this, this.querySelector('[role="dialog"]'));
      },
      { once: true }
    );
  }

  closeFacets(event) {
    event.preventDefault();

    if (!this.facetsWrapper) return;

    this.facetsWrapper.classList.remove("active");
    removeTrapFocus(this.focusElement);
    document.body.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden-mobile-facets");
  }

  onHandleKeyDown(event) {
    if (
      event.key === "Escape" &&
      this.facetsWrapper?.classList.contains("active")
    ) {
      this.closeFacets(event);
    }
  }

  handleShowButtonsClick(event) {
    event.preventDefault();

    // targetId - FacetFiltersForm or FacetSortForm
    const targetId =
      event.currentTarget.dataset.desktopModal ||
      event.currentTarget.dataset.mobileModal;

    const targetForm = document.querySelector(
      `[data-filters-form="${targetId}"]`
    );
    if (!targetForm) return;
    targetForm.handleFacetButtonClick(event);
  }
}

FacetFiltersForm.openStateKey = "facets-open-filters";
FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

// Metropolitan shows a price filter as two number inputs (From / To) — no range slider —
// so this element only keeps the pair in a valid order.
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = Array.from(this.querySelectorAll(".field__input"));
    if (this.inputs.length < 2) return;

    this.inputs.forEach((input) => {
      input.addEventListener("change", this.onRangeChange.bind(this));
    });
  }

  // Both fields keep the collection range as their bounds; the pair is put in
  // order only once a value is committed, so a partially typed number is never
  // clamped against the other field.
  onRangeChange() {
    const [minInput, maxInput] = this.inputs;

    this.clampToRange(minInput);
    this.clampToRange(maxInput);

    if (minInput.value === "" || maxInput.value === "") return;

    const from = Number(minInput.value);
    const to = Number(maxInput.value);

    if (from > to) {
      minInput.value = to;
      maxInput.value = from;
    }
  }

  clampToRange(input) {
    if (input.value === "") return;

    const value = Number(input.value);

    if (Number.isNaN(value)) {
      input.value = "";
      return;
    }

    const min = Number(input.getAttribute("min")) || 0;
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (max && value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    if (event.currentTarget?.getAttribute?.("aria-disabled") === "true") return;
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
